
import { useState } from 'react';
import { CandidateAssessment } from '../utils/assessmentEvaluation';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { ListChecks, FileText, AlertTriangle, Clock, Heart, CheckCircle, XCircle } from 'lucide-react';

interface CandidateAssessmentDetailsProps {
  assessment: CandidateAssessment;
  mcqQuestions: any[];
  descriptiveQuestions: any[];
  personalityQuestions?: any[];
}

const CandidateAssessmentDetails = ({ 
  assessment, 
  mcqQuestions, 
  descriptiveQuestions,
  personalityQuestions = []
}: CandidateAssessmentDetailsProps) => {
  const [activeTab, setActiveTab] = useState('summary');
  
  if (!assessment) {
    return <div className="text-center p-8">No assessment data available</div>;
  }
  
  const { 
    mcqScore, 
    descriptiveScore, 
    overallScore, 
    aiEvaluation, 
    personalityEvaluation,
    cheatingFlags, 
    mcqAnswers, 
    descriptiveAnswers,
    personalityAnswers = {},
    completedAt 
  } = assessment;
  
  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-amber-600";
    if (score >= 40) return "text-orange-600";
    return "text-red-600";
  };
  
  const hasCheatingFlags = cheatingFlags && 
    (cheatingFlags.tabSwitched || cheatingFlags.blurred || cheatingFlags.aiDetected || cheatingFlags.timeExpired);
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <Tabs defaultValue="summary" onValueChange={setActiveTab} value={activeTab}>
        <div className="border-b border-gray-200">
          <div className="p-4">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="summary" className="flex items-center gap-1.5">
                <CheckCircle size={16} />
                <span>Summary</span>
              </TabsTrigger>
              <TabsTrigger value="mcq" className="flex items-center gap-1.5">
                <ListChecks size={16} />
                <span>Multiple Choice</span>
              </TabsTrigger>
              <TabsTrigger value="descriptive" className="flex items-center gap-1.5">
                <FileText size={16} />
                <span>Technical</span>
              </TabsTrigger>
              <TabsTrigger value="personality" className="flex items-center gap-1.5">
                <Heart size={16} />
                <span>Behavioral</span>
              </TabsTrigger>
            </TabsList>
          </div>
        </div>
        
        <div className="p-4">
          <TabsContent value="summary" className="mt-0">
            <div className="space-y-4">
              {/* Score card */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Assessment Scores</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-500 mb-1">MCQ Score</div>
                      <div className={`text-2xl font-bold ${getScoreColor(mcqScore)}`}>
                        {mcqScore}%
                      </div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-500 mb-1">Technical</div>
                      <div className={`text-2xl font-bold ${getScoreColor(descriptiveScore || 0)}`}>
                        {descriptiveScore || 'N/A'}%
                      </div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-sm text-blue-600 mb-1">Overall Score</div>
                      <div className={`text-2xl font-bold ${getScoreColor(overallScore)}`}>
                        {overallScore}%
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Flags */}
              {hasCheatingFlags && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center text-amber-700">
                      <AlertTriangle className="mr-2" size={18} />
                      Integrity Concerns
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {cheatingFlags.tabSwitched && (
                        <div className="flex items-center text-amber-800">
                          <XCircle className="mr-2 flex-shrink-0" size={16} />
                          <span>Candidate switched tabs during assessment</span>
                        </div>
                      )}
                      {cheatingFlags.blurred && (
                        <div className="flex items-center text-amber-800">
                          <XCircle className="mr-2 flex-shrink-0" size={16} />
                          <span>Candidate left the assessment window</span>
                        </div>
                      )}
                      {cheatingFlags.aiDetected && (
                        <div className="flex items-center text-amber-800">
                          <XCircle className="mr-2 flex-shrink-0" size={16} />
                          <span>AI-generated content detected in answers</span>
                        </div>
                      )}
                      {cheatingFlags.timeExpired && (
                        <div className="flex items-center text-amber-800">
                          <Clock className="mr-2 flex-shrink-0" size={16} />
                          <span>Time expired before completion</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Technical AI Evaluation */}
              {aiEvaluation && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Technical Evaluation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[200px] rounded-md border p-4">
                      <div className="whitespace-pre-wrap text-sm">{aiEvaluation}</div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
              
              {/* Personality AI Evaluation */}
              {personalityEvaluation && (
                <Card className="border-blue-100">
                  <CardHeader className="pb-2 bg-blue-50 border-b border-blue-100">
                    <CardTitle className="text-lg flex items-center">
                      <Heart className="mr-2 text-blue-500" size={18} />
                      Behavioral & Cultural Fit Assessment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ScrollArea className="h-[200px] rounded-md border p-4">
                      <div className="whitespace-pre-wrap text-sm">{personalityEvaluation}</div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
              
              <div className="text-xs text-gray-500">
                Assessment completed on {completedAt}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="mcq" className="mt-0">
            <div className="space-y-4">
              {mcqQuestions.map((question, index) => {
                const candidateAnswer = mcqAnswers[question.id];
                const isCorrect = candidateAnswer === question.correctAnswer;
                
                return (
                  <Card key={question.id} className={`${isCorrect ? 'border-green-200' : 'border-red-200'}`}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between">
                        <Badge variant={isCorrect ? "success" : "destructive"} className="mb-2">
                          {isCorrect ? 'Correct' : 'Incorrect'}
                        </Badge>
                        <span className="text-xs text-gray-500">Question {index + 1}</span>
                      </div>
                      <CardTitle className="text-base">{question.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {question.options.map((option: any) => (
                          <div 
                            key={option.id}
                            className={`p-3 rounded-md ${
                              option.id === question.correctAnswer
                                ? 'bg-green-50 border border-green-200'
                                : option.id === candidateAnswer
                                  ? 'bg-red-50 border border-red-200'
                                  : 'bg-gray-50 border border-gray-200'
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-6 h-6 flex items-center justify-center mr-2 rounded-full border border-current
                                ${option.id === question.correctAnswer ? 'text-green-500' : 
                                  option.id === candidateAnswer ? 'text-red-500' : 'text-gray-400'}`}
                              >
                                {option.id === question.correctAnswer && <CheckCircle size={14} />}
                                {option.id === candidateAnswer && option.id !== question.correctAnswer && <XCircle size={14} />}
                                {option.id !== candidateAnswer && option.id !== question.correctAnswer && option.id}
                              </div>
                              <span>{option.text}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
          
          <TabsContent value="descriptive" className="mt-0">
            <div className="space-y-6">
              {descriptiveQuestions.map((question, index) => {
                const answer = descriptiveAnswers[question.id] || 'No answer provided';
                
                return (
                  <Card key={question.id}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-end">
                        <span className="text-xs text-gray-500">Question {index + 1}</span>
                      </div>
                      <CardTitle className="text-base">{question.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mb-2">
                        <div className="text-sm whitespace-pre-wrap">{answer}</div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
          
          <TabsContent value="personality" className="mt-0">
            <div className="space-y-6">
              {personalityQuestions && personalityQuestions.map((question, index) => {
                const answer = personalityAnswers[question.id] || 'No answer provided';
                
                return (
                  <Card key={question.id} className="border-blue-100">
                    <CardHeader className="pb-2">
                      <div className="flex justify-end">
                        <span className="text-xs text-gray-500">Question {index + 1}</span>
                      </div>
                      <CardTitle className="text-base">{question.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-blue-50 p-4 rounded-md border border-blue-100 mb-2">
                        <div className="text-sm whitespace-pre-wrap">{answer}</div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {(!personalityQuestions || personalityQuestions.length === 0) && (
                <div className="text-center p-6 text-gray-500">
                  No personality questions available for this assessment
                </div>
              )}
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default CandidateAssessmentDetails;
